import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Select,
  DatePicker,
  Space,
  Tag,
  Popconfirm,
  message,
  Typography,
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  adminPromoCodeService,
  type PromoCode,
  type PromoCodeInput,
  type PromoScope,
} from '../../services/promoCodeService';
import { adminService, type User } from '../../services/adminService';

const { Title, Text } = Typography;

const PromoCodeManagement: React.FC = () => {
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<PromoCode | null>(null);
  const [form] = Form.useForm();

  // Option sources for the pickers (loaded lazily on first modal open).
  const [users, setUsers] = useState<User[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [products, setProducts] = useState<{ SupersedeNo: string; Description: string }[]>([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  const scope = Form.useWatch<PromoScope>('scope', form);
  const isPublic = Form.useWatch<boolean>('isPublic', form);
  const discountType = Form.useWatch<string>('discountType', form);

  const load = async () => {
    setLoading(true);
    try {
      setCodes(await adminPromoCodeService.list());
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const ensureOptions = async () => {
    if (optionsLoaded) return;
    try {
      const [userRes, cats, prodRes] = await Promise.all([
        adminService.getUsers({ page: 1, limit: 500 }),
        adminService.getProductCategories(),
        adminService.getProducts({ page: 1, limit: 1000, status: 'all' }),
      ]);
      setUsers(userRes.data);
      setCategories(cats);
      setProducts(prodRes.products.map((p: any) => ({ SupersedeNo: p.SupersedeNo, Description: p.Description })));
      setOptionsLoaded(true);
    } catch (err: any) {
      message.error('Failed to load pickers (customers/products). You can still edit other fields.');
    }
  };

  const openCreate = async () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      discountType: 'percent',
      scope: 'order',
      isPublic: true,
      isActive: true,
    });
    setModalOpen(true);
    ensureOptions();
  };

  const openEdit = async (c: PromoCode) => {
    setEditing(c);
    form.setFieldsValue({
      code: c.Code,
      description: c.Description ?? '',
      discountType: c.DiscountType,
      discountValue: Number(c.DiscountValue),
      scope: c.Scope,
      scopeTargets: c.scopeTargets,
      isPublic: c.IsPublic,
      customerIds: c.customerIds,
      minOrderAmount: c.MinOrderAmount ?? undefined,
      perCustomerLimit: c.PerCustomerLimit ?? undefined,
      totalUseLimit: c.TotalUseLimit ?? undefined,
      dateRange: c.StartDate || c.EndDate
        ? [c.StartDate ? dayjs(c.StartDate) : null, c.EndDate ? dayjs(c.EndDate) : null]
        : undefined,
      isActive: c.IsActive,
    });
    setModalOpen(true);
    ensureOptions();
  };

  const handleSubmit = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return; // inline validation errors shown by antd
    }

    const [start, end] = values.dateRange ?? [];
    const payload: PromoCodeInput = {
      code: values.code.trim(),
      description: values.description?.trim() || null,
      discountType: values.discountType,
      discountValue: Number(values.discountValue),
      scope: values.scope,
      isPublic: !!values.isPublic,
      minOrderAmount: values.minOrderAmount ?? null,
      perCustomerLimit: values.perCustomerLimit ?? null,
      totalUseLimit: values.totalUseLimit ?? null,
      startDate: start ? dayjs(start).format('YYYY-MM-DD') : null,
      endDate: end ? dayjs(end).format('YYYY-MM-DD') : null,
      isActive: !!values.isActive,
      customerIds: values.isPublic ? [] : (values.customerIds ?? []),
      scopeTargets: values.scope === 'order' ? [] : (values.scopeTargets ?? []),
    };

    setSaving(true);
    try {
      if (editing) {
        await adminPromoCodeService.update(editing.PromoID, payload);
        message.success('Promo code updated');
      } else {
        await adminPromoCodeService.create(payload);
        message.success('Promo code created');
      }
      setModalOpen(false);
      load();
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to save promo code');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c: PromoCode) => {
    try {
      await adminPromoCodeService.setActive(c.PromoID, !c.IsActive);
      message.success(c.IsActive ? 'Promo code deactivated' : 'Promo code activated');
      load();
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to update status');
    }
  };

  const discountLabel = (c: PromoCode) =>
    c.DiscountType === 'percent' ? `${Number(c.DiscountValue)}%` : `SGD ${Number(c.DiscountValue).toFixed(2)}`;

  const validityLabel = (c: PromoCode) => {
    const s = c.StartDate ? dayjs(c.StartDate).format('DD MMM YY') : '—';
    const e = c.EndDate ? dayjs(c.EndDate).format('DD MMM YY') : '—';
    if (!c.StartDate && !c.EndDate) return <Text type="secondary">Always</Text>;
    return <Text>{s} → {e}</Text>;
  };

  const usageLabel = (c: PromoCode) => {
    const cap = c.TotalUseLimit != null ? `/${c.TotalUseLimit}` : '';
    return `${c.redemptionCount}${cap}`;
  };

  const columns = [
    {
      title: 'Code', dataIndex: 'Code', key: 'Code',
      render: (v: string, r: PromoCode) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v}</Text>
          {r.Description ? <Text type="secondary" style={{ fontSize: 12 }}>{r.Description}</Text> : null}
        </Space>
      ),
    },
    {
      title: 'Discount', key: 'discount', align: 'right' as const,
      render: (_: any, r: PromoCode) => <Tag color="geekblue">{discountLabel(r)}</Tag>,
    },
    {
      title: 'Scope', dataIndex: 'Scope', key: 'Scope',
      render: (v: PromoScope, r: PromoCode) => (
        <Space direction="vertical" size={0}>
          <Tag>{v}</Tag>
          {v !== 'order' && r.scopeTargets.length > 0
            ? <Text type="secondary" style={{ fontSize: 12 }}>{r.scopeTargets.length} target(s)</Text>
            : null}
        </Space>
      ),
    },
    {
      title: 'Audience', dataIndex: 'IsPublic', key: 'IsPublic',
      render: (v: boolean, r: PromoCode) =>
        v ? <Tag color="green">Public</Tag>
          : <Tag color="orange">{r.customerIds.length} customer(s)</Tag>,
    },
    { title: 'Validity', key: 'validity', render: (_: any, r: PromoCode) => validityLabel(r) },
    { title: 'Used', key: 'usage', align: 'right' as const, render: (_: any, r: PromoCode) => usageLabel(r) },
    {
      title: 'Status', dataIndex: 'IsActive', key: 'IsActive',
      render: (v: boolean) => v ? <Tag color="blue">Active</Tag> : <Tag>Inactive</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 200,
      render: (_: any, r: PromoCode) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button>
          <Popconfirm
            title={r.IsActive ? 'Deactivate this code?' : 'Activate this code?'}
            description={r.IsActive ? 'Customers can no longer redeem it.' : 'Customers will be able to redeem it.'}
            onConfirm={() => toggleActive(r)}
            okText={r.IsActive ? 'Deactivate' : 'Activate'}
            cancelText="Cancel"
          >
            <Button size="small" danger={r.IsActive}>{r.IsActive ? 'Deactivate' : 'Activate'}</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const userOptions = useMemo(
    () => users.map((u) => ({
      value: u.UserID,
      label: `${u.UserName || u.UserID}${u.UserEmail ? ` (${u.UserEmail})` : ''}`,
    })),
    [users]
  );
  const productOptions = useMemo(
    () => products.map((p) => ({ value: p.SupersedeNo, label: `${p.SupersedeNo} — ${p.Description}` })),
    [products]
  );
  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c, label: c })),
    [categories]
  );

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Promo Codes</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Promo Code</Button>
      </Space>

      <Card>
        <Table
          rowKey="PromoID"
          dataSource={codes}
          columns={columns}
          loading={loading}
          scroll={{ x: 900 }}
          pagination={false}
          locale={{ emptyText: 'No promo codes yet. Click "New Promo Code" to add one.' }}
        />
      </Card>

      <Modal
        title={editing ? 'Edit Promo Code' : 'New Promo Code'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText={editing ? 'Save' : 'Create'}
        confirmLoading={saving}
        width={640}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="code"
            label="Code"
            rules={[{ required: true, message: 'Code is required' }]}
            tooltip="What the customer types at checkout (case-insensitive)."
          >
            <Input placeholder="e.g. WELCOME10" style={{ textTransform: 'uppercase' }} />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input placeholder="Internal / customer-facing label" />
          </Form.Item>

          <Space style={{ display: 'flex' }} align="start">
            <Form.Item
              name="discountType"
              label="Discount Type"
              rules={[{ required: true }]}
              style={{ flex: 1 }}
            >
              <Select
                options={[
                  { value: 'percent', label: 'Percentage (%)' },
                  { value: 'fixed', label: 'Fixed amount (SGD)' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="discountValue"
              label={discountType === 'fixed' ? 'Amount (SGD)' : 'Percent (%)'}
              rules={[
                { required: true, message: 'Required' },
                { type: 'number', min: 0.01, message: 'Must be > 0' },
                ...(discountType === 'percent'
                  ? [{ type: 'number' as const, max: 100, message: 'Max 100%' }]
                  : []),
              ]}
              style={{ flex: 1 }}
            >
              <InputNumber min={0.01} step={1} style={{ width: '100%' }} />
            </Form.Item>
          </Space>

          <Form.Item
            name="scope"
            label="Applies To"
            rules={[{ required: true }]}
            tooltip="Order = whole cart. Product/Category = only matching lines are discounted."
          >
            <Select
              options={[
                { value: 'order', label: 'Whole order' },
                { value: 'product', label: 'Specific products' },
                { value: 'category', label: 'Specific categories' },
              ]}
            />
          </Form.Item>

          {scope === 'product' && (
            <Form.Item
              name="scopeTargets"
              label="Eligible Products"
              rules={[{ required: true, message: 'Select at least one product' }]}
            >
              <Select
                mode="multiple"
                showSearch
                optionFilterProp="label"
                placeholder="Search products…"
                options={productOptions}
              />
            </Form.Item>
          )}

          {scope === 'category' && (
            <Form.Item
              name="scopeTargets"
              label="Eligible Categories"
              rules={[{ required: true, message: 'Select at least one category' }]}
            >
              <Select
                mode="multiple"
                showSearch
                optionFilterProp="label"
                placeholder="Search categories…"
                options={categoryOptions}
              />
            </Form.Item>
          )}

          <Form.Item
            name="isPublic"
            label="Public (anyone can use)"
            valuePropName="checked"
            tooltip="Off = only the customers you assign can redeem it."
          >
            <Switch />
          </Form.Item>

          {!isPublic && (
            <Form.Item
              name="customerIds"
              label="Assigned Customers"
              rules={[{ required: true, message: 'Assign at least one customer' }]}
            >
              <Select
                mode="multiple"
                showSearch
                optionFilterProp="label"
                placeholder="Search customers…"
                options={userOptions}
              />
            </Form.Item>
          )}

          <Form.Item name="minOrderAmount" label="Minimum Order (SGD)" tooltip="Blank = no minimum.">
            <InputNumber min={0} step={1} style={{ width: '100%' }} placeholder="No minimum" />
          </Form.Item>

          <Space style={{ display: 'flex' }} align="start">
            <Form.Item
              name="perCustomerLimit"
              label="Per-customer Limit"
              tooltip="Blank = unlimited."
              style={{ flex: 1 }}
            >
              <InputNumber min={1} step={1} style={{ width: '100%' }} placeholder="Unlimited" />
            </Form.Item>
            <Form.Item
              name="totalUseLimit"
              label="Total Use Limit"
              tooltip="Blank = unlimited."
              style={{ flex: 1 }}
            >
              <InputNumber min={1} step={1} style={{ width: '100%' }} placeholder="Unlimited" />
            </Form.Item>
          </Space>

          <Form.Item name="dateRange" label="Validity Period" tooltip="Blank = always valid.">
            <DatePicker.RangePicker style={{ width: '100%' }} allowEmpty={[true, true]} />
          </Form.Item>

          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PromoCodeManagement;
